import axios from 'axios';
import tokenManager from './tokenManager';
import { getDeviceId } from './deviceId';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

let isRedirecting = false;

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Attach the persistent device id for compliance/audit logging (e.g. the
    // registration audit record). Harmless on other requests.
    if (!config.headers) config.headers = {};
    config.headers['X-Device-Id'] = getDeviceId();

    const isPublicAuthEndpoint =
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/register') ||
      config.url?.includes('/auth/refresh');

    if (!isPublicAuthEndpoint && tokenManager.hasToken()) {
      if (tokenManager.isTokenExpiring()) {
        try { await refreshAccessToken(); } catch { /* will get 401 */ }
      }
      const token = tokenManager.getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }

    const isFormData =
      (typeof FormData !== 'undefined' && config.data instanceof FormData) ||
      Object.prototype.toString.call(config.data) === '[object FormData]' ||
      (config.data && typeof config.data.append === 'function');

    if (isFormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
      }
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Public endpoints — never redirect to login on 401
      if (originalRequest.url?.includes('/complaints/guest')) throw error;
      if (originalRequest.url?.includes('/auth/')) throw error;

      originalRequest._retry = true;
      try {
        await refreshAccessToken();
        const token = tokenManager.getAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshErr) {
        tokenManager.clearTokens();
        const msg = refreshErr?.response?.data?.message || error?.response?.data?.message || 'Your session has expired. Please log in again.';
        redirectToLogin('expired', msg);
        throw error;
      }
    }

    if (error.response?.status === 403) {
      throw { ...error, message: 'You do not have permission to perform this action' };
    }
    if (error.response?.status >= 500) {
      throw { ...error, message: 'Server error. Please try again later.' };
    }
    if (!error.response) {
      throw { ...error, message: 'Network error. Please check your connection.' };
    }

    throw error;
  }
);

// Cookie is sent automatically — no body needed
export async function refreshAccessToken() {
  const existingPromise = tokenManager.getRefreshPromise();
  if (existingPromise) return existingPromise;

  const refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const { accessToken, expiresIn } = response.data;
      tokenManager.setAccessToken(accessToken, expiresIn);
      return accessToken;
    } finally {
      tokenManager.clearRefreshPromise();
    }
  })();

  tokenManager.setRefreshPromise(refreshPromise);
  return refreshPromise;
}

function redirectToLogin(reason = 'expired', message = 'Your session has expired. Please log in again.') {
  if (isRedirecting) return;
  isRedirecting = true;
  tokenManager.clearTokens();

  if (reason === 'expired') {
    window.dispatchEvent(new CustomEvent('session:expired', { detail: { message } }));
  }

  setTimeout(() => {
    window.location.replace('/login');
    isRedirecting = false;
  }, 1500);
}

export default api;

export function getRedirectPath() {
  const path = sessionStorage.getItem('redirectAfterLogin');
  sessionStorage.removeItem('redirectAfterLogin');
  return path || '/';
}